import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { FormikProvider, Form, FormikProps } from 'formik';
import { useAtomValue } from 'jotai';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { QaqcKpiTemplate } from '../types';
import { useQaQcForm } from '../hooks/useQaQcForm';
import * as kpiState from '../state';
import { TemplateFieldTypeEnum } from '@/__generated__/graphql';
import { GenericDialog } from '@/components/shared/GenericDialog';
import { PageLoader } from '@/components/shared/PageLoader';
import { abyssColors } from '@/theme/colors';
import {
  accordionStyles,
  accordianDetailsStyles,
  getAccordionSummaryStyles,
} from '../components/Panel/styles';
import { buildFieldByKey, emptyValuesForFieldKeys, type QaqcFormValues } from './utils';
import { QaqcFieldControl } from './QaqcFieldControl';
import { QaqcPanelFooter } from './QaqcPanelFooter';
import { QaqcPanelHeader } from './QaqcPanelHeader';
import { QaQcPanelNoTemplateState } from './QaQcPanelNoTemplateState';
import { JobPhaseRemarks } from './JobPhaseRemarks';
import { AttachmentsSection } from '../components/Panel/AttachmentsSection';

type Props = {
  onClose: () => void;
};

export const QaQcPanel = ({ onClose }: Props) => {
  const openIntent = useAtomValue(kpiState.qaqcPanelOpenIntent);
  const intent = openIntent ?? 'fill';
  const [editUnlocked, setEditUnlocked] = useState(() => intent !== 'view');

  useEffect(() => {
    setEditUnlocked(intent !== 'view');
  }, [intent]);

  const isReadOnly = intent === 'view' && !editUnlocked;

  const {
    formik,
    template,
    templateLoading,
    logEntriesLoading,
    isSubmitting,
    templateError,
    submitWithSaveKind,
    remarksText,
    setRemarksText,
    resetRemarksFromServer,
    isQaqcPanelDirty,
  } = useQaQcForm();

  const wasSubmittingReference = useRef(false);
  useEffect(() => {
    if (wasSubmittingReference.current && !isSubmitting && intent === 'view' && editUnlocked) {
      setEditUnlocked(false);
    }
    wasSubmittingReference.current = isSubmitting;
  }, [isSubmitting, intent, editUnlocked]);

  const fieldByKey = useMemo(() => buildFieldByKey(template), [template]);

  const sortedSections = useMemo(() => {
    if (!template?.qaqcConfig?.length) return [];
    return [...template.qaqcConfig].sort((a, b) => a.order - b.order);
  }, [template?.qaqcConfig]);

  if (templateLoading || logEntriesLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <PageLoader variant="sm" />
      </Box>
    );
  }

  if (templateError) {
    return (
      <Stack sx={{ p: 3 }}>
        <Alert severity="error">Failed to load job phase templates. Please try again later.</Alert>
      </Stack>
    );
  }

  if (!template?.qaqcConfig?.length) {
    return <QaQcPanelNoTemplateState onClose={onClose} />;
  }

  let panelFooter: ReactNode;
  if (isReadOnly) {
    panelFooter = <QaqcPanelFooter variant="view" onEditJobPhase={() => setEditUnlocked(true)} />;
  } else if (intent === 'view' && editUnlocked) {
    panelFooter = (
      <QaqcPanelFooter
        variant="editSaved"
        onCancel={() => {
          formik.resetForm();
          resetRemarksFromServer();
          setEditUnlocked(false);
        }}
        onSave={() => void submitWithSaveKind('edited')}
        isSubmitting={isSubmitting}
        isDirty={isQaqcPanelDirty}
      />
    );
  } else {
    panelFooter = (
      <QaqcPanelFooter
        variant="fill"
        onCancel={onClose}
        isSubmitting={isSubmitting}
        isDirty={isQaqcPanelDirty}
      />
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <FormikProvider value={formik}>
        <Form
          noValidate
          style={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            height: '100%',
            overflow: 'hidden',
          }}
        >
          <QaqcPanelHeader onClose={onClose} />

          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              overflowX: 'hidden',
              px: 2,
              py: 2,
              minHeight: 0,
            }}
          >
            <Stack spacing={2}>
              {sortedSections.map((section) => (
                <QaqcSectionBlock
                  key={section.sectionKey}
                  section={section}
                  template={template}
                  fieldByKey={fieldByKey}
                  formik={formik}
                  readOnly={isReadOnly}
                />
              ))}
              <JobPhaseRemarks
                value={remarksText}
                onChange={setRemarksText}
                readOnly={isReadOnly}
                disabled={isSubmitting}
              />
              <AttachmentsSection
                placement="qaqcForm"
                qaqcLogsTemplateId={template.id}
                readOnly={isReadOnly}
              />
            </Stack>
          </Box>

          {panelFooter}
        </Form>
      </FormikProvider>
    </Box>
  );
};

type SectionBlockProps = {
  section: NonNullable<QaqcKpiTemplate['qaqcConfig']>[number];
  template: QaqcKpiTemplate;
  fieldByKey: ReturnType<typeof buildFieldByKey>;
  formik: FormikProps<QaqcFormValues>;
  readOnly: boolean;
};

const QaqcSectionBlock = ({
  section,
  template,
  fieldByKey,
  formik,
  readOnly,
}: SectionBlockProps) => {
  const [expanded, setExpanded] = useState(section.defaultOpen ?? true);
  const sortedTables = useMemo(
    () => [...section.tables].sort((a, b) => a.order - b.order),
    [section.tables]
  );

  return (
    <Accordion
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      disableGutters
      sx={accordionStyles}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={getAccordionSummaryStyles(expanded)}>
        <Typography
          variant="h6"
          sx={{ fontWeight: 700, fontSize: '1.4rem', color: abyssColors.primary[500] }}
        >
          {section.title}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={accordianDetailsStyles}>
        <Stack spacing={3}>
          {sortedTables.map((tableConfig) => {
            const tableIndex = formik.values.tables.findIndex(
              (t) => t.sectionKey === section.sectionKey && t.tableKey === tableConfig.tableKey
            );
            if (tableIndex < 0) {
              return null;
            }
            return (
              <QaqcTableBlock
                key={`${section.sectionKey}-${tableConfig.tableKey}`}
                template={template}
                fieldByKey={fieldByKey}
                formik={formik}
                tableIndex={tableIndex}
                tableConfig={tableConfig}
                readOnly={readOnly}
                showTableTitle={section.allowsMultipleSectionBlocks}
              />
            );
          })}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

type TableBlockProps = {
  template: QaqcKpiTemplate;
  fieldByKey: ReturnType<typeof buildFieldByKey>;
  formik: FormikProps<QaqcFormValues>;
  tableIndex: number;
  tableConfig: NonNullable<NonNullable<QaqcKpiTemplate['qaqcConfig']>[number]['tables']>[number];
  readOnly: boolean;
  showTableTitle: boolean;
};

const QaqcTableBlock = ({
  template,
  fieldByKey,
  formik,
  tableIndex,
  tableConfig,
  readOnly,
  showTableTitle,
}: TableBlockProps) => {
  const [pendingDeleteIndex, setPendingDeleteIndex] = useState<number | null>(null);
  const table = formik.values.tables[tableIndex];
  if (!table) {
    return null;
  }

  const addEntry = () => {
    const keys = table.fieldKeys;
    const next = {
      entryIndex: table.entries.length + 1,
      values: emptyValuesForFieldKeys(keys, fieldByKey),
    };
    void formik.setFieldValue(`tables.${tableIndex}.entries`, [...table.entries, next]);
  };

  const removeEntryAt = (arrayIndex: number) => {
    if (table.entries.length <= 1) {
      return;
    }
    const next = table.entries
      .filter((_, index) => index !== arrayIndex)
      .map((entry, index) => ({
        entryIndex: index + 1,
        values: structuredClone(entry.values),
      }));
    void formik.setFieldValue(`tables.${tableIndex}.entries`, next);
  };

  return (
    <Box>
      {showTableTitle ? (
        <Typography
          variant="h6"
          sx={{ fontWeight: 700, fontSize: '1.4rem', color: abyssColors.primary[500], mb: 1.5 }}
        >
          {tableConfig.title}
        </Typography>
      ) : null}
      <Stack spacing={2}>
        {table.entries.map((entry, entryIndex) => (
          <Box
            key={`${table.sectionKey}-${table.tableKey}-row-${entryIndex}`}
            sx={{
              p: 2,
              borderRadius: 1,
              backgroundColor: abyssColors.primary[50],
              border: `1px solid ${abyssColors.primary[100]}`,
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 1.5 }}
            >
              <Typography variant="h6" color={abyssColors.primary[500]} fontWeight={500}>
                Entry {entryIndex + 1}
              </Typography>
              {!readOnly && table.supportsMultipleEntries && table.entries.length > 1 ? (
                <IconButton
                  type="button"
                  size="small"
                  onClick={() => setPendingDeleteIndex(entryIndex)}
                  edge="end"
                >
                  <DeleteOutlineIcon fontSize="small" sx={{ color: abyssColors.primary[500] }} />
                </IconButton>
              ) : null}
            </Stack>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 2,
              }}
            >
              {table.fieldKeys.map((fieldKey) => {
                const field = fieldByKey.get(fieldKey);
                const shouldSpanFullRow = Boolean(
                  field?.allowsMultipleValues || field?.type === TemplateFieldTypeEnum.MultiSelect
                );

                return (
                  <Box
                    key={fieldKey}
                    sx={{
                      gridColumn: { xs: 'auto', sm: shouldSpanFullRow ? '1 / -1' : 'auto' },
                      minWidth: 0,
                    }}
                  >
                    <QaqcFieldControl
                      template={template}
                      formik={formik}
                      fieldKey={fieldKey}
                      tableIndex={tableIndex}
                      entryIndex={entryIndex}
                      disabled={readOnly}
                    />
                  </Box>
                );
              })}
            </Box>
          </Box>
        ))}
      </Stack>
      {!readOnly && table.supportsMultipleEntries ? (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
          <Button
            type="button"
            size="small"
            startIcon={<AddIcon fontSize="small" />}
            onClick={addEntry}
            sx={{ textTransform: 'none' }}
          >
            Add Entry
          </Button>
        </Box>
      ) : null}

      <GenericDialog
        open={pendingDeleteIndex !== null}
        onClose={() => setPendingDeleteIndex(null)}
        title="Remove entry"
        content={
          <Typography fontWeight={400} fontSize="14px" color={abyssColors.primary[500]}>
            Are you sure you want to remove this entry?
          </Typography>
        }
        actions={
          <>
            <Button
              type="button"
              variant="text"
              onClick={() => setPendingDeleteIndex(null)}
              sx={{ color: abyssColors.primary[500], textTransform: 'none' }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="contained"
              onClick={() => {
                if (pendingDeleteIndex !== null) {
                  removeEntryAt(pendingDeleteIndex);
                }
                setPendingDeleteIndex(null);
              }}
              sx={{ textTransform: 'none' }}
            >
              Remove
            </Button>
          </>
        }
        actionsStyles={{ mr: 3, pb: 3 }}
      />
    </Box>
  );
};
